import { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getAllMaterialGroups } from "services/material-groups.service";
import Loading from 'components/center-loading'
import { SnackBar } from "components/uikit-adapter";
import { getFamiliesByMaterialGroup } from "services/families.service";

const BudgetFamilyContext = createContext();

const BudgetFamilyProvider = ({ children }) => {
  const [materialGroups, setMaterialGroups] = useState([]);
  const [familiesForFilter, setFamiliesForFilter] = useState([]);
  const [requestCounter, setRequestCounter] = useState(0);
  const [snackBarContent, setSnackBarContent] = useState({ showFeedback: false, messageFeedBack: '' });

  const fetchMaterialGroups = async () => {
    try {
      startFetching();
      const result = await getAllMaterialGroups();
      setMaterialGroups(result);
    } catch (error) {
      console.error("Failed to fetch material groups:", error);
      setMaterialGroups([]);
    } finally {
      stopFetching();
    }
  };

  const fetchFamiliesForFilter = async (materialGroupId) => {
    try {
      startFetching();
      const result = await getFamiliesByMaterialGroup(materialGroupId);
      setFamiliesForFilter(result);
    } catch (error) {
      console.error("Failed to fetch families:", error);
      setFamiliesForFilter([]);
    } finally {
      stopFetching();
    }
  };

  const startFetching = () => {
    setRequestCounter(prevState => prevState + 1);
  }
  
  const stopFetching = () => {
    setRequestCounter(prevState => prevState - 1);
  };

  const handleShowFeedback = (message) => {
    setSnackBarContent({ showFeedback: true, messageFeedBack: message });
  };

  const closeFeedback = () => {
    setSnackBarContent({ showFeedback: false, messageFeedBack: '' });
  };

  useEffect(() => {
    fetchMaterialGroups();
  }, []);

  const values = useMemo(() => ({
    materialGroups, 
    startFetching, 
    stopFetching, 
    handleShowFeedback, 
    fetchFamiliesForFilter, 
    familiesForFilter, 
    setFamiliesForFilter
  }), [materialGroups, familiesForFilter])

  return (
    <BudgetFamilyContext.Provider
      value={values}
    >
      <Loading isLoading={requestCounter > 0} />
      {children}
      <SnackBar
        message={snackBarContent.messageFeedBack}
        open={snackBarContent.showFeedback}
        autoHideDuration={3000}
        onRequestClose={closeFeedback}
      />
    </BudgetFamilyContext.Provider>
  )
}

BudgetFamilyProvider.propTypes = {
  children: PropTypes.any.isRequired
}

export const useBudgetFamilyContext = () => useContext(BudgetFamilyContext);

export { BudgetFamilyProvider, BudgetFamilyContext };
